// The pure view model of the "Complete client links" dialog (SEP-300).
//
// The dialog keeps ONE Client Book row in context: the Account and its person
// Client. It shows the links of that row together, it lets the user pick a
// record and a contact, and it sends all the changes as ONE request. This
// module holds every decision of the dialog, so the component only renders
// and a unit test can read each rule:
//
//   - `currentClientLinks` splits the panel rows of the Account, the record and
//     the Client into what is RECORDED and what is INFERRED through a record
//     (`via`). An inferred row is never shown as a recorded link;
//   - `searchOutcome` gives a distinct outcome for no match, one match and
//     several matches. The dialog never picks a match for the user, and it
//     never matches on a name;
//   - `planClientLinks` turns the choices into the review list, the conflict
//     warnings, the reasons that stop a save, and the request.

import type {
    ClientLinkReplacementMode,
    CompleteClientLinksRequest,
    RelationshipAppliesTo,
    RelationshipRoleDto,
    RelationshipRow,
    TriTierClientRow,
    TriTierLinkStatus,
} from '@septima/contract';

// ---------------------------------------------------------------------------
// The current links of the row
// ---------------------------------------------------------------------------

/** One recorded link, from the side of the fixed endpoint. */
export interface RecordedLink {
    readonly relationshipId: string;
    readonly endpointId: string;
    /** Null when the other end is outside the read of the caller. */
    readonly name: string | null;
    readonly reference: string | null;
    readonly roleLabel: string;
}

/** One contact that the server infers through a record. Nothing records it. */
export interface InferredContact {
    readonly contactId: string;
    readonly name: string | null;
    /** The record that the link runs through, named for a person. */
    readonly viaName: string | null;
}

export interface CurrentClientLinks {
    /** The active record link of the Account, or null. */
    readonly record: RecordedLink | null;
    /** The active contact links of the target record: the record that the
     *  user picked, else the record that owns the Account now. */
    readonly recordContacts: readonly RecordedLink[];
    /** The contacts that the Account reaches through a record. */
    readonly inferredContacts: readonly InferredContact[];
    /** The active contact links recorded on the Account itself (for example an
     *  accountant). They are recorded links, and not the record contacts. */
    readonly accountContacts: readonly RecordedLink[];
    /** The active direct contact links of the person Client. */
    readonly anchors: readonly RecordedLink[];
}

function isActive(row: RelationshipRow): boolean {
    return row.endDate === null;
}

function recorded(row: RelationshipRow): RecordedLink {
    return {
        relationshipId: row.relationshipId,
        endpointId: row.counterpartId,
        name: row.counterpartName,
        reference: row.counterpartReference,
        roleLabel: row.counterpartRole.label,
    };
}

/**
 * The links of one row, from three panel reads: the Account, the record that
 * the dialog targets (the current one, or the one the user picked), and the
 * person Client. A read that does not apply gives an empty list.
 */
export function currentClientLinks(input: {
    readonly accountRows: readonly RelationshipRow[];
    readonly recordRows: readonly RelationshipRow[];
    readonly clientRows: readonly RelationshipRow[];
}): CurrentClientLinks {
    const accountActive = input.accountRows.filter(isActive);
    const recordRow = accountActive.find(
        (row) => row.via === null && row.counterpartType === 'suitability-record',
    );
    return {
        record: recordRow ? recorded(recordRow) : null,
        recordContacts: input.recordRows
            .filter((row) => isActive(row) && row.via === null && row.counterpartType === 'contact')
            .map(recorded),
        inferredContacts: accountActive
            .filter((row) => row.via !== null && row.counterpartType === 'contact')
            .map((row) => ({
                contactId: row.counterpartId,
                name: row.counterpartName,
                viaName: row.via?.name ?? null,
            })),
        accountContacts: accountActive
            .filter((row) => row.via === null && row.counterpartType === 'contact')
            .map(recorded),
        anchors: input.clientRows
            .filter(
                (row) =>
                    isActive(row) &&
                    row.via === null &&
                    row.counterpartType === 'contact' &&
                    row.kind === 'contact_client',
            )
            .map(recorded),
    };
}

/** True when the row offers "Complete client links". */
export function needsClientLinks(status: TriTierLinkStatus): boolean {
    return status !== 'complete';
}

/** What the row lacks, in the words of the button hint and the dialog. */
export const LINK_STATUS_LABELS: Record<TriTierLinkStatus, string> = {
    complete: 'All links recorded',
    no_suitability_record: 'No suitability record',
    no_contact: 'No contact on the record',
    anchor_differs: 'Client contact not on the record',
};

// ---------------------------------------------------------------------------
// Search
// ---------------------------------------------------------------------------

export type SearchOutcome = 'idle' | 'loading' | 'no_match' | 'one_match' | 'several_matches';

/**
 * The outcome of one user search. An empty search is `idle`: the dialog lists
 * nothing until the user types, so it never offers a match that the user did
 * not ask for.
 */
export function searchOutcome(search: string, matches: number, isLoading: boolean): SearchOutcome {
    if (search.trim() === '') return 'idle';
    if (isLoading) return 'loading';
    if (matches === 0) return 'no_match';
    return matches === 1 ? 'one_match' : 'several_matches';
}

/** The message under a search box, or null when it needs none. `noun` is the
 *  plural tier word, for example "suitability records". */
export function searchOutcomeMessage(
    outcome: SearchOutcome,
    matches: number,
    noun: string,
): string | null {
    switch (outcome) {
        case 'no_match':
            return `No ${noun} match this search. Check the spelling, or search by reference.`;
        case 'one_match':
            return `1 match. Select it to use it.`;
        case 'several_matches':
            return `${matches} ${noun} match. Check the details before you select one.`;
        default:
            return null;
    }
}

// ---------------------------------------------------------------------------
// Roles
// ---------------------------------------------------------------------------

/** The active catalogue roles of one kind, in catalogue order. */
export function rolesFor(
    roles: readonly RelationshipRoleDto[],
    kind: RelationshipAppliesTo,
): RelationshipRoleDto[] {
    return roles.filter((role) => role.active && role.appliesTo === kind);
}

/** The role to preselect: the one active role of the kind, else none. A kind
 *  with a choice of roles waits for the user. */
export function defaultRole(
    roles: readonly RelationshipRoleDto[],
    kind: RelationshipAppliesTo,
): string {
    const of = rolesFor(roles, kind);
    return of.length === 1 ? (of[0]?.key ?? '') : '';
}

// ---------------------------------------------------------------------------
// The plan
// ---------------------------------------------------------------------------

export interface ClientLinksDraft {
    /** The record that the user picked, or '' to keep the current one. */
    readonly recordId: string;
    readonly recordRole: string;
    /** What happens to the current record link when the user picks another. */
    readonly recordReplaceMode: ClientLinkReplacementMode | null;
    /** The contact that the user picked for the record, or ''. */
    readonly contactId: string;
    readonly contactRole: string;
    /** An optional current contact link of the record to replace, or ''. */
    readonly contactReplaceId: string;
    readonly contactReplaceMode: ClientLinkReplacementMode | null;
    /** True only after the user asks for a direct client contact. */
    readonly anchorWanted: boolean;
    readonly anchorContactId: string;
    readonly anchorRole: string;
    /** An optional current direct contact link to replace, or ''. */
    readonly anchorReplaceId: string;
    readonly anchorReplaceMode: ClientLinkReplacementMode | null;
}

export const EMPTY_DRAFT: ClientLinksDraft = {
    recordId: '',
    recordRole: '',
    recordReplaceMode: null,
    contactId: '',
    contactRole: '',
    contactReplaceId: '',
    contactReplaceMode: null,
    anchorWanted: false,
    anchorContactId: '',
    anchorRole: '',
    anchorReplaceId: '',
    anchorReplaceMode: null,
};

export type ClientLinkChangeKind = 'create' | 'unchanged';

export interface ClientLinkChange {
    readonly step: 'record' | 'contact' | 'client_anchor';
    readonly kind: ClientLinkChangeKind;
    /** One line for the review list. */
    readonly summary: string;
    /** The link that this change ends or deletes, in words, or null. */
    readonly replaces: string | null;
}

export interface ClientLinksPlan {
    readonly changes: readonly ClientLinkChange[];
    /** Conflicts that the user must read. */
    readonly warnings: readonly string[];
    /** Why the dialog cannot save yet. Empty when it can. */
    readonly blockers: readonly string[];
    /** The request, or null while a blocker stands. */
    readonly request: CompleteClientLinksRequest | null;
}

/** The names that the dialog shows for the picks, from the option labels. */
export interface ClientLinksLabels {
    readonly record: string | null;
    readonly contact: string | null;
    readonly anchorContact: string | null;
}

function nameOf(link: RecordedLink): string {
    if (link.name && link.reference) return `${link.name} (${link.reference})`;
    return link.name ?? link.reference ?? 'a row outside your book';
}

function replacementWords(link: RecordedLink, mode: ClientLinkReplacementMode): string {
    return mode === 'end'
        ? `Ends the link to ${nameOf(link)} today and keeps it as history`
        : `Deletes the wrong link to ${nameOf(link)}`;
}

/**
 * The plan of the dialog. Each step is optional. A pick that equals a recorded
 * link is `unchanged` and stays out of the request. A pick that would replace a
 * recorded link needs an explicit choice of `end` or `delete` first.
 */
export function planClientLinks(input: {
    readonly row: TriTierClientRow;
    readonly current: CurrentClientLinks;
    readonly draft: ClientLinksDraft;
    readonly labels: ClientLinksLabels;
}): ClientLinksPlan {
    const { row, current, draft, labels } = input;
    const changes: ClientLinkChange[] = [];
    const warnings: string[] = [];
    const blockers: string[] = [];
    const request: {
        -readonly [K in keyof CompleteClientLinksRequest]: CompleteClientLinksRequest[K];
    } = { accountId: row.account.accountId };

    // 1. The record.
    const recordChanges = draft.recordId !== '' && draft.recordId !== current.record?.endpointId;
    if (draft.recordId !== '' && !recordChanges) {
        changes.push({
            step: 'record',
            kind: 'unchanged',
            summary: `${labels.record ?? 'The record'} already owns this account`,
            replaces: null,
        });
    } else if (recordChanges) {
        const name = labels.record ?? 'the selected record';
        let replaces: string | null = null;
        if (current.record) {
            warnings.push(
                `${nameOf(current.record)} already owns this account. Choose to end or delete that link.`,
            );
            if (draft.recordReplaceMode === null) {
                blockers.push('Choose what happens to the current suitability record link.');
            } else {
                replaces = replacementWords(current.record, draft.recordReplaceMode);
            }
        }
        if (draft.recordRole === '') blockers.push('Select the role of the suitability record.');
        changes.push({
            step: 'record',
            kind: 'create',
            summary: `Link ${name} to this account`,
            replaces,
        });
        request.record = {
            suitabilityRecordId: draft.recordId,
            role: draft.recordRole,
            ...(current.record && draft.recordReplaceMode
                ? {
                      replace: {
                          relationshipId: current.record.relationshipId,
                          mode: draft.recordReplaceMode,
                      },
                  }
                : {}),
        };
    }

    // 2. A contact on the target record.
    if (draft.contactId !== '') {
        const hasRecord = draft.recordId !== '' || current.record !== null;
        // `recordContacts` are the contacts of the TARGET record: the record
        // that the user picked, else the current one.
        const already = current.recordContacts.find((link) => link.endpointId === draft.contactId);
        if (!hasRecord) {
            blockers.push('Select a suitability record before you link a contact.');
        }
        if (already) {
            changes.push({
                step: 'contact',
                kind: 'unchanged',
                summary: `${labels.contact ?? nameOf(already)} is already on the record`,
                replaces: null,
            });
        } else {
            const replaced = current.recordContacts.find(
                (link) => link.relationshipId === draft.contactReplaceId,
            );
            if (replaced && draft.contactReplaceMode === null) {
                blockers.push('Choose to end or delete the contact link that you replace.');
            }
            if (draft.contactRole === '') blockers.push('Select the role of the contact.');
            changes.push({
                step: 'contact',
                kind: 'create',
                summary: `Link ${labels.contact ?? 'the selected contact'} to the record`,
                replaces:
                    replaced && draft.contactReplaceMode
                        ? replacementWords(replaced, draft.contactReplaceMode)
                        : null,
            });
            request.contact = {
                contactId: draft.contactId,
                role: draft.contactRole,
                ...(replaced && draft.contactReplaceMode
                    ? {
                          replace: {
                              relationshipId: replaced.relationshipId,
                              mode: draft.contactReplaceMode,
                          },
                      }
                    : {}),
            };
        }
    }

    // 3. The direct client contact, only on explicit request.
    if (draft.anchorWanted) {
        if (row.clientId === null) {
            blockers.push(
                'This account has no single client, so it cannot take a direct client contact.',
            );
        } else if (draft.anchorContactId === '') {
            blockers.push('Select the direct client contact, or clear the box.');
        } else {
            const already = current.anchors.find(
                (link) => link.endpointId === draft.anchorContactId,
            );
            if (already) {
                changes.push({
                    step: 'client_anchor',
                    kind: 'unchanged',
                    summary: `${labels.anchorContact ?? nameOf(already)} is already a direct client contact`,
                    replaces: null,
                });
            } else {
                const replaced = current.anchors.find(
                    (link) => link.relationshipId === draft.anchorReplaceId,
                );
                const others = current.anchors.filter((link) => link !== replaced);
                if (others.length > 0) {
                    warnings.push(
                        `The client already has a direct contact: ${others.map(nameOf).join(', ')}. That link stays unless you choose to replace it.`,
                    );
                }
                if (replaced && draft.anchorReplaceMode === null) {
                    blockers.push(
                        'Choose to end or delete the direct client contact that you replace.',
                    );
                }
                if (draft.anchorRole === '')
                    blockers.push('Select the role of the direct client contact.');
                changes.push({
                    step: 'client_anchor',
                    kind: 'create',
                    summary: `Record ${labels.anchorContact ?? 'the selected contact'} as a direct client contact`,
                    replaces:
                        replaced && draft.anchorReplaceMode
                            ? replacementWords(replaced, draft.anchorReplaceMode)
                            : null,
                });
                request.clientAnchor = {
                    clientId: row.clientId,
                    contactId: draft.anchorContactId,
                    role: draft.anchorRole,
                    ...(replaced && draft.anchorReplaceMode
                        ? {
                              replace: {
                                  relationshipId: replaced.relationshipId,
                                  mode: draft.anchorReplaceMode,
                              },
                          }
                        : {}),
                };
            }
        }
    }

    const sends = Boolean(request.record || request.contact || request.clientAnchor);
    if (!sends && blockers.length === 0) {
        blockers.push(
            changes.length > 0
                ? 'Nothing to change: these links are already recorded.'
                : 'Select a suitability record or a contact to link.',
        );
    }
    return {
        changes,
        warnings,
        blockers,
        request: blockers.length === 0 ? request : null,
    };
}
